// The unit menu (made over 2026-11-03): a compact grid of unit cards, so a
// computer shows most units at once and a phone a good few. Tapping a unit
// goes straight to its games if it has one part, or pops its parts open right
// over the card (each with a peek at its pictures). Which units and parts
// show, and their stars, are worked out as they always were.

import "./index.scss";
import "../../App.scss";
import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import {
  fetchImage,
  redirectFromParams,
  compare,
  getSearchParams,
  findSingleGameData,
  findSquishName,
} from "../nonUIFuncs";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getBottomPercentageValues } from "../../Store/store";
import { store } from "../../Store";
import PropTypes from "prop-types";
import { debug } from "../../utils/debug";

const Stars = ({ count }) => (
  <span className="um2_stars" aria-hidden="true">
    {[0, 1, 2].map((i) => (
      <span key={i} className={`um2_star${i < count ? " um2_star--on" : ""}`}>
        ★
      </span>
    ))}
  </span>
);

Stars.propTypes = { count: PropTypes.number };

const UnitCard = ({ unit, order, onOpen }) => (
  <button
    type="button"
    id={`unit-${unit.Key}`}
    className="um2_card"
    style={{ "--order": Math.min(order, 14) }}
    onClick={(e) => onOpen(unit, e.currentTarget)}
    aria-label={`View ${unit.unitName} unit`}
  >
    <span className="um2_cardName aboriginalSans">{unit.unitName}</span>
    <span className="um2_cardPicture" style={{ backgroundImage: `url(${fetchImage(unit.image)})` }} />
    <Stars count={unit.displayStars} />
  </button>
);

UnitCard.propTypes = { unit: PropTypes.object, order: PropTypes.number, onOpen: PropTypes.func };

/** The parts of a unit, popped open over its card. */
const PartsPopup = ({ open, stories, linkFor, onClose, dataset }) => {
  const boxRef = useRef(null);
  const [place, setPlace] = useState(null);
  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const r = open.rect;
    const w = box.offsetWidth;
    const h = box.offsetHeight;
    const left = Math.min(window.innerWidth - w - 10, Math.max(10, r.left + r.width / 2 - w / 2));
    const top = Math.min(window.innerHeight - h - 10, Math.max(10, r.top + r.height * 0.2));
    setPlace({ left, top, originX: r.left + r.width / 2 - left, originY: r.top + r.height / 2 - top });
    box.querySelector("a")?.focus();
  }, [open]);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return createPortal(
    <div className="um2_scrim" onClick={onClose} role="presentation">
      <div
        ref={boxRef}
        className={`um2_popup${place ? " um2_popup--placed" : ""}`}
        style={place ? { left: place.left, top: place.top, transformOrigin: `${place.originX}px ${place.originY}px` } : undefined}
        role="dialog"
        aria-label={`${open.unit.unitName}: pick a part`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="um2_popupHead">
          <span className="um2_popupTitle aboriginalSans">{open.unit.unitName}</span>
          <button type="button" className="um2_close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="um2_parts">
          {open.parts.map((part, i) => (
            <Link key={part.subUnit} to={linkFor(part)} className="um2_part" style={{ "--order": i }}>
              <span className="um2_partName badaBoom">
                {stories ? `Part ${part.subUnit[part.subUnit.length - 1]}` : part.subUnit}
              </span>
              <span className="um2_peek" aria-hidden="true">
                {part.data
                  .filter((d) => d.App_Art)
                  .slice(0, 4)
                  .map((d) => (
                    <span
                      key={`${d.Key}${d.App_Art}`}
                      className="um2_peekPicture"
                      style={{ backgroundImage: `url(${fetchImage(d.App_Art, dataset)})` }}
                    />
                  ))}
              </span>
            </Link>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
};

PartsPopup.propTypes = {
  open: PropTypes.object,
  stories: PropTypes.bool,
  linkFor: PropTypes.func,
  onClose: PropTypes.func,
  dataset: PropTypes.string,
};

const UnitMenu = ({ scoreData, config, stories, dataset }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = getSearchParams(location);
  const difficultyParam = params.find((param) => param.id === "difficulty");
  const locationParam = params.find((param) => param.id === "location")?.value;
  const unitsRef = useRef([]);
  const [units, setUnits] = useState([]);
  const [open, setOpen] = useState(null); // { unit, parts, rect }
  unitsRef.current = units;
  useEffect(() => {
    document.title = `${config.AppName} - Units`;
    let requiredParams = config.hasLocationSelection
      ? ["difficulty", "location"]
      : ["difficulty"];
    let redirectPath = redirectFromParams(requiredParams) || "";
    if (redirectPath.length > 0) {
      navigate(redirectPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (stories) {
      findDifficultyGames({
        gameData: store.getState().store.value.gameData,
        vocabData: store.getState().store.value.storybookVocab,
      });
    } else {
      findDifficultyGames({
        gameData: store.getState().store.value.gameData,
        vocabData: store.getState().store.value.vocabData,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoreData]);
  let unitArr = [];
  let unitNumArr = [];
  const findDifficultyGames = ({ gameData, vocabData }) => {
    let games = [];
    scoreData.forEach((d) => {
      if (
        d.difficultyScores.easy.difficulty === Number(difficultyParam.value)
      ) {
        games.push({
          ...d.difficultyScores.easy,
          category: d.category,
          gameName: d.gameName,
          gameType: d.gameType,
          key: `${d.category}${d.gameName}Easy`,
        });
      } else if (
        d.difficultyScores.medium.difficulty === Number(difficultyParam.value)
      ) {
        games.push({
          ...d.difficultyScores.medium,
          category: d.category,
          gameName: d.gameName,
          gameType: d.gameType,
          key: `${d.category}${d.gameName}Medium`,
        });
      } else if (
        d.difficultyScores.hard.difficulty === Number(difficultyParam.value)
      ) {
        games.push({
          ...d.difficultyScores.hard,
          category: d.category,
          gameName: d.gameName,
          gameType: d.gameType,
          key: `${d.category}${d.gameName}Hard`,
        });
      }
    });
    // setDifficultyGames(games);
    findUnits({ games: games, gameData: gameData, vocabData: vocabData });
  };
  const getImageName = ({ lesson }) => {
    if (stories) {
      return `Category_Selection_Story_${lesson}.png`;
    } else {
      return `Category_Selection_${lesson}1.png`;
    }
  };
  const findUnits = ({ games, gameData, vocabData }) => {
    vocabData.forEach((datum) => {
      if (
        !unitNumArr.includes(datum.lesson) &&
        typeof Number(datum.lesson) === "number"
      ) {
        unitNumArr.push(datum.lesson);
        unitArr.push({
          Key: Number(datum.lesson),
          name: `Unit ${datum.lesson}`,
          unitName: datum.homeName,
          image: getImageName({ lesson: datum.lesson }),
          currentStars: 0,
          totalStars: 0,
          displayStars: 0,
        });
      }
      unitArr.sort((a, b) => compare(a.Key, b.Key));
    });
    findStars({ unitArr: unitArr, games: games, gameData: gameData });
  };
  const findStars = ({ unitArr, games, gameData }) => {
    let testTotal = 0;
    let testCurrent = 0;
    let adjustedUnits = unitArr.map((unit) => {
      let currentStars = 0;
      let totalStars = 0;
      let displayStars = 0;
      games.forEach((d) => {
        if (d.category === unit.unitName) {
          currentStars = currentStars + d.stars;
          let goldStar = store
            .getState()
            .store.value.goldStar.find((g) => g.game === findSquishName(d.gameType));
          if (goldStar?.secondType !== "none") {
            totalStars = totalStars + 6;
          } else {
            totalStars = totalStars + 3;
          }
        }
      });
      testTotal = testTotal + totalStars;
      testCurrent = testCurrent + currentStars;
      let percent = (currentStars / totalStars) * 100;
      if (percent >= 99) {
        displayStars = 3;
      } else if (percent >= 50 && percent < 99) {
        displayStars = 2;
      } else if (percent >= 1 && percent < 50) {
        displayStars = 1;
      } else {
        displayStars = 0;
      }
      let difficulty = 5;
      let dataToUse = stories
        ? store.getState().store.value.storybookVocab
        : store.getState().store.value.vocabData;
      let unitData = dataToUse.filter((d) => d.homeName === unit.unitName);
      let subUnits = [];
      unitData.forEach((d) => {
        if (d.rank !== undefined) {
          if (Number(d.rank) < difficulty) {
            difficulty = Number(d.rank);
          }
        } else {
          difficulty = 1;
        }
        if (!subUnits.includes(d.subCat)) {
          subUnits.push(d.subCat);
        }
      });
      return {
        ...unit,
        totalStars: totalStars,
        currentStars: currentStars,
        displayStars: displayStars,
        difficulty: difficulty,
        subUnits: subUnits.map((str) => {
          const filtered = findSingleGameData(
            str,
            unitData,
            locationParam,
            config.dialectStructure,
          ).filter(
            (datum) =>
              datum.rank === undefined ||
              Number(datum.rank) <= Number(difficultyParam.value),
          );
          return {
            subCat: str,
            data: filtered,
          };
        }),
      };
    });
    let uniqueCategories = [];
    adjustedUnits = adjustedUnits.filter((d) => {
      if (!uniqueCategories.includes(d.unitName)) {
        uniqueCategories.push(d.unitName);
        return true;
      }
      return false;
    });
    let categoriesWGames = [];
    gameData.forEach((d) => {
      if (!categoriesWGames.includes(d.category.toUpperCase())) {
        categoriesWGames.push(d.category.toUpperCase());
      }
    });
    let filteredUnits = adjustedUnits.filter((d) =>
      categoriesWGames.includes(d.unitName.toUpperCase()),
    );
    dispatch(getBottomPercentageValues(testCurrent, testTotal));
    filteredUnits = filteredUnits.filter(
      (d) => d.difficulty <= Number(difficultyParam.value),
    );
    filteredUnits = filteredUnits.filter((d) => {
      let check = false;
      let filteredSubUnits = d.subUnits.filter((datum) => {
        let subUnitCheck = false;
        if (datum.data.length >= 8) {
          let gameInfo = store
            .getState()
            .store.value.gameData.find(
              (game) =>
                game.category === datum.data[0].homeName &&
                game.target === datum.subCat,
            );
          if (gameInfo !== undefined) {
            for (let i = 0; i <= config.PossibleGames.length - 1; i++) {
              if (
                gameInfo[config.PossibleGames[i]] !== null &&
                Number(gameInfo[config.PossibleGames[i]]) <=
                  Number(difficultyParam.value)
              ) {
                subUnitCheck = true;
              }
            }
          } else {
            debug.warn("Missing category in data:", datum);
          }
        }
        return subUnitCheck;
      });
      for (let i = 0; i <= filteredSubUnits.length - 1; i++) {
        if (filteredSubUnits[i].data.length >= 8) {
          check = true;
          break;
        }
      }
      return check;
    });
    setUnits(filteredUnits);
  };
  /** A unit's parts that have games at this level (unchanged rules). */
  const partsFor = (unit) => {
    let unitData = [];
    let dataToUse = stories
      ? store.getState().store.value.storybookVocab
      : store.getState().store.value.vocabData;
    let data = dataToUse.filter((d) => d.homeName === unit.unitName);
    let subUnits = [];
    data.forEach((d) => {
      if (!subUnits.includes(d.subCat)) {
        subUnits.push(d.subCat);
      }
    });
    let subUnitFinal = [];
    subUnits.forEach((d) => {
      let unitDataTemp = data.filter(
        (datum) => datum.subCat === d && datum.homeName === unit.unitName,
      );
      for (let i = 0; i <= unitDataTemp.length - 1; i++) {
        if (
          Number(difficultyParam.value) >= Number(unitDataTemp[i].rank) ||
          unitDataTemp[i].rank === undefined
        ) {
          let gameData = store
            .getState()
            .store.value.gameData.find(
              (datum) => datum.target === d && datum.category === unit.unitName,
            );
          if (gameData !== undefined) {
            let check = false;
            for (let j = 0; j <= config.PossibleGames.length - 1; j++) {
              let game = config.PossibleGames[j];
              if (gameData[game] !== null) {
                if (unitDataTemp.length >= 8) {
                  check = true;
                  break;
                }
              }
            }
            if (check) {
              subUnitFinal.push(d);
              break;
            }
          } else {
            debug.warn("Missing category:", `"${d}"`);
          }
        }
      }
    });
    subUnitFinal.forEach((d) => {
      let uData = findSingleGameData(
        d,
        data,
        locationParam,
        config.dialectStructure,
      ).filter(
        (datum) =>
          datum.rank === undefined ||
          Number(datum.rank) <= Number(difficultyParam.value),
      );
      unitData.push({ unit: unit, subUnit: d, data: uData });
    });
    return unitData;
  };
  const linkFor = (part) => ({
    pathname: `/${stories ? "story-" : ""}choose-game-type`,
    search: `${location.search}&unit=${part.unit.Key + part.unit.unitName}&game-name=${part.subUnit}`,
  });

  // one part: straight to its games; more: pop them open over the card
  const openUnit = (unit, card) => {
    const parts = partsFor(unit);
    if (parts.length === 1) {
      navigate(linkFor(parts[0]));
      return;
    }
    if (parts.length === 0) return;
    setOpen({ unit, parts, rect: card.getBoundingClientRect(), card });
  };
  const closeParts = () => {
    open?.card?.focus();
    setOpen(null);
  };

  return (
    <div className="all_pages_container unitmenu_container um2_screen">
      <div className="um2_banner">
        <span className="um2_guide" aria-hidden="true" />
        <h1 className="um2_bannerText badaBoom">Pick a unit!</h1>
      </div>
      <div className="um2_grid" role="list">
        {unitsRef.current.map((unit, i) => (
          <div key={unit.name} role="listitem" className="um2_cell">
            <UnitCard unit={unit} order={i} onOpen={openUnit} />
          </div>
        ))}
      </div>
      {open && <PartsPopup open={open} stories={stories} linkFor={linkFor} onClose={closeParts} dataset={dataset} />}
    </div>
  );
};

export default UnitMenu;

UnitMenu.propTypes = {
  scoreData: PropTypes.array,
  config: PropTypes.object,
  stories: PropTypes.bool,
  dataset: PropTypes.string,
};
